import Basil from 'basil.js';
import sha256 from 'js-sha256';
import postRobot from '../utils/post-robot.min';

// Track a cookie (yes, always a cookie, not localStorage) to detect whether
// the user has ever logged in.  This is used by src/auth-xd/index.html as
// required by Safari tracking protections.
const EVER_LOGGED_IN_COOKIE = 'bxcauth';

// Export a variable that lets us know if we are inside Switcher Studio
export const isSwitcherStudioAppShell =
  window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.switcher;

// The Storage class wraps localStorage and cookies to protect against Private Mode on Safari shenanigans
class Storage {
  constructor() {
    if (isSwitcherStudioAppShell) {
      console.warn('localStorage will not work; falling back to custom Switcher Studio strategy');
      this.strategy = new SwitcherStudioStorageStrategy();
    } else {
      try {
        this.strategy = Basil.localStorage;
        this.set('__sentinel__', 'SENTINEL');
        if (this.get('__sentinel__') != 'SENTINEL') {
          throw 'localStorage non functional';
        }
      } catch (e) {
        console.warn('localStorage not working; falling back to cookies', e);
        this.strategy = Basil.cookie;
      } finally {
        this.remove('__sentinel__');
      }
    }
  }

  get(key) {
    return this.strategy.get(key);
  }

  getPromise(key) {
    if (this.strategy.getPromise) {
      return this.strategy.getPromise(key);
    } else {
      return new Promise((resolve, _) => resolve(this.strategy.get(key)));
    }
  }

  set(key, value) {
    return this.strategy.set(key, value);
  }

  setPromise(key, value) {
    if (this.strategy.setPromise) {
      return this.strategy.setPromise(key, value);
    } else {
      return new Promise((resolve, _) => resolve(this.strategy.set(key, value)));
    }
  }

  remove(key) {
    return this.strategy.remove(key);
  }

  clear() {
    this._clearLoggedInCookie();
    return this.strategy.reset();
  }

  bumpLoggedInCookie() {
    Basil.cookie.set(EVER_LOGGED_IN_COOKIE, '1', {
      expireDays: 365,
      sameSite: 'none',
      secure: true,
      domain: 'boxcast.com',
    });
  }

  _clearLoggedInCookie() {
    Basil.cookie.remove(EVER_LOGGED_IN_COOKIE);
  }
}

// The SwitcherStudioStorageStrategy implements a special type of storage layer
// that can mostly work inside their nested webview+iframe implementation.  Note
// that syncronous storage operations are not permitted.
class SwitcherStudioStorageStrategy {
  get(key) {
    console.warn('Switcher Studio does not support a synchronous storage.get implementation');
    return null;
  }
  set(key, value) {
    console.warn('Switcher Studio does not support a synchronous storage.set implementation');
    this.setPromise(key, value);
  }
  remove(key) {
    console.warn('Switcher Studio does not support removing from storage');
    return postRobot.send(
      window.parent,
      'ui',
      {
        method: 'setItem',
        params: { key, value: '' },
      },
      { timeout: 2500 }
    );
  }
  reset() {
    console.warn('Switcher Studio does not support clearing storage');
    this.remove('oauthState');
    this.remove('oauthVerifier');
    this.remove('oauthPath');
    this.remove('oauthToken');
  }
  getPromise(key) {
    return postRobot
      .send(
        window.parent,
        'ui',
        {
          method: 'getItem',
          params: { key },
        },
        { timeout: 2500 }
      )
      .then((r) => r.data);
  }
  setPromise(key, value) {
    return postRobot.send(
      window.parent,
      'ui',
      {
        method: 'setItem',
        params: { key, value },
      },
      { timeout: 2500 }
    );
  }
}

// Export an instance of the storage class to be used throughout this page lifecycle
export var storage = new Storage();

// Export a helper method for immediately redirecting to the login page
export function gotoLogin(message = null) {
  //$('body > app').hide();
  //$('body').append(`<h1 class="text-center">Redirecting in 5 sec ${message}</h1>`);
  //setTimeout(() => {
  let state = generateRandomString(8);
  let verifier = generateRandomString(48);
  let challenge = b64(sha256.array(verifier));
  let promises = [
    storage.setPromise('oauthState', state),
    storage.setPromise('oauthVerifier', verifier),
    storage.setPromise('oauthPath', window.location.href),
  ];
  Promise.all(promises).then(() => {
    let authUrl = isSwitcherStudioAppShell ? SWITCHER_AUTH_URL : AUTH_URL;
    window.location.replace(
      authUrl + '&state=' + state + '&code_challenge_method=S256&code_challenge=' + challenge
    );
  });
  //}, 5000)
  throw new Error('halt');
}

// Export a function to retrieve the current auth token
export function getAuthToken() {
  // XXX: The Switcher Studio app may inject the OAuth token into the document. Prefer that,
  // but resort to localStorage for all other requests.
  return document.switcherStudioAccessToken || storage.get('oauthToken');
}

function generateRandomString(n) {
  var bytes = new Uint8Array((n * 3 + 3) / 4);
  if (window.msCrypto) {
    msCrypto.getRandomValues(bytes);
  } else {
    crypto.getRandomValues(bytes);
  }
  return b64(bytes).substring(0, n);
}

function b64(bytes) {
  return btoa(String.fromCharCode.apply(null, bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}



// WEBPACK FOOTER //
// ./app/auth/utils.js